import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Maximize2, Mic, Volume2, VolumeX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useChat } from "../hooks/useChat";
import { useVoice } from "../hooks/useVoice";
import { useSpeech } from "../hooks/useSpeech";
import ChatMessages from "./ChatMessages";

const chips = ["Book a court for me", "Which membership fits a beginner?", "What are tonight's open slots?"];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const { messages, sendMessage, streaming } = useChat();
  const { listening, toggle, supported } = useVoice((text) =>
    setInput((prev) => (prev ? prev + " " : "") + text)
  );
  const speech = useSpeech();
  const prevStreaming = useRef(false);

  useEffect(() => {
    if (prevStreaming.current && !streaming && speech.enabled && open) {
      const last = messages[messages.length - 1];
      if (last?.role === "assistant" && last.content) speech.speak(last.content);
    }
    prevStreaming.current = streaming;
  }, [streaming, messages, speech, open]);

  const submit = (text) => {
    if (streaming) return;
    speech.stop();
    sendMessage(text);
    setInput("");
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[520px] max-h-[70vh] [max-height:calc(100dvh-8.5rem)] rounded-2xl backdrop-blur-xl bg-black/85 border border-white/15 shadow-[0_0_40px_rgba(204,255,0,0.15)] flex flex-col overflow-hidden"
            data-testid="chat-widget-panel"
            role="dialog"
            aria-label="AI assistant chat"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#0f0f10]">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-[#ccff00] flex items-center justify-center font-display text-black">A</span>
                <div>
                  <p className="font-bold text-sm">Ace — AI Concierge</p>
                  <p className="text-[11px] text-white/50">Ask anything, any language</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {speech.supported && (
                  <button
                    onClick={speech.toggle}
                    data-testid="chat-widget-speaker"
                    aria-label={speech.enabled ? "Turn off spoken replies" : "Turn on spoken replies"}
                    title={speech.enabled ? "Spoken replies ON" : "Spoken replies OFF"}
                    className={`p-2 transition-colors ${speech.enabled ? "text-[#ccff00]" : "text-white/60 hover:text-[#ccff00]"}`}
                  >
                    {speech.enabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  </button>
                )}
                <Link to="/assistant" onClick={() => setOpen(false)} className="p-2 text-white/60 hover:text-[#ccff00] transition-colors" aria-label="Open full-screen chat" data-testid="chat-widget-expand">
                  <Maximize2 size={16} />
                </Link>
                <button onClick={() => setOpen(false)} className="p-2 text-white/60 hover:text-white transition-colors" aria-label="Close chat" data-testid="chat-widget-close">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {messages.length === 0 && (
                <div className="text-white/60 text-sm space-y-3">
                  <p>Hi! I'm Ace. Ask me about courts, coaching, memberships — or anything else at all.</p>
                  <div className="flex flex-wrap gap-2">
                    {chips.map((c) => (
                      <button
                        key={c}
                        onClick={() => submit(c)}
                        data-testid="chat-widget-chip"
                        className="text-xs border border-[#ccff00]/40 text-[#ccff00] px-3 py-1.5 rounded-full hover:bg-[#ccff00]/10 transition-colors"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <ChatMessages messages={messages} streaming={streaming} compact />
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit(input);
              }}
              className="p-3 border-t border-white/10 flex gap-2 bg-[#0f0f10]"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Ace anything…"
                data-testid="chat-widget-input"
                aria-label="Chat message"
                className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#ccff00]/60 placeholder:text-white/35"
              />
              {supported && (
                <button
                  type="button"
                  onClick={toggle}
                  data-testid="chat-widget-mic"
                  aria-label={listening ? "Stop listening" : "Start voice input"}
                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                    listening ? "bg-red-500 text-white" : "border border-white/20 text-white/60 hover:text-[#ccff00] hover:border-[#ccff00]/60"
                  }`}
                >
                  <Mic size={15} />
                </button>
              )}
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-widget-send"
                aria-label="Send message"
                className="w-10 h-10 rounded-full bg-[#ccff00] text-black flex items-center justify-center disabled:opacity-40 hover:bg-[#a3e635] transition-colors"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        className="fixed bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-[#ccff00] text-black flex items-center justify-center glow-pulse hover:scale-110 transition-transform duration-200"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </>
  );
};

export default ChatWidget;
